import { Component, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError, take, tap } from 'rxjs/operators';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AuthService, ILoginResult } from '../../services/auth.service';
import { CommonService } from '../../services/common.service';
import { IPageDataViewModel } from '../../pages/pages';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss']
})
export class AuthComponent implements OnInit {

  private readonly storageKey = 'auth';

  constructor(private authService: AuthService, private activatedRoute: ActivatedRoute, private router: Router, private commonService: CommonService) { }

  /**
   *
   */
  ngOnInit() {
    this.vm$ = this.activatedRoute.queryParamMap.pipe(
      map((pm: ParamMap) => {
        const uid: string = pm.has('uid') ? pm.get('uid') : undefined;
        this.authService.tokenUserIdCache(uid);

        const vm: IAuthViewModel = {
          tokenVm: environment.deploymentType != 'chauffeur' ? undefined : {
            userName: uid,
            password: undefined
          },
          loginVm: environment.deploymentType == 'chauffeur' ? undefined : {
            userName: localStorage.getItem(this.storageKey),
            password: ''
          }
        };
        return vm;
      })
    );
  }

  vm$: Observable<IAuthViewModel>;

  workingText: string;
  errorText: string;

  errorContinue() {
    this.errorText = undefined;
  }

  /**
   * send the token to the user, based upon the AccessId in the URL
   */
  async tokenRequest(vm: ITokenViewModel): Promise<void> {
    this.workingText = 'Requesting access code...'

    try {
      const paramData = {
        tokenAccessId: vm.userName
      };
      const getDataResult: IRequestTokenResult = await this.commonService.getData('PageCommand', 'token-auth', 'request', paramData, '/api/eclsweb20').toPromise();

      this.errorText = getDataResult.errorText ? getDataResult.errorText
        : getDataResult.data?.status == "ok" ? undefined
          : getDataResult.data?.status ?? 'Missing status';
      vm.tokenRequested = getDataResult.data?.status == "ok";

    } catch (err) {
      this.errorText = err;
    }

    this.workingText = undefined
  }

  /**
   * user name / password or uid / token
   */
  async login(vm: ILoginViewModel): Promise<void> {

    this.workingText = 'Logging in...'

    await of({}).pipe(
      switchMap(() =>
        this.authService.login(vm.userName, vm.password).pipe(
          take(1),
          tap((v: ILoginResult) => {
            if (v.status == 'ok') {
              localStorage.setItem(this.storageKey, v.user.userName);
              const returnUrl = this.activatedRoute.snapshot.queryParams['return'] || '/';
              this.router.navigateByUrl(returnUrl);
            } else {
              this.errorText = v.status;
            }
          })
        )
      ),
      catchError(e => {
        this.errorText = e.toString();
        return of({});
      })
    ).toPromise();

    this.workingText = undefined
  }

}

interface IAuthViewModel {
  loginVm: ILoginViewModel;
  tokenVm: ITokenViewModel;
}

interface ITokenViewModel extends ILoginViewModel {
  tokenRequested?: boolean;
}

/**
 *
 */
interface ILoginViewModel {
  userName: string;
  password: string;
}

interface IRequestTokenResult extends IPageDataViewModel {
  data?: {
    status: string;
  };
}
